import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  debtAdjustments,
  type DebtAdjustmentListItem,
} from '@/api/debtAdjustments';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TYPE_LABEL: Record<string, string> = {
  MUTUAL_OFFSET: 'Взаємозалік',
  TRANSFER: 'Перенос боргу',
  WRITE_OFF: 'Списання боргу',
  OFF_BALANCE_WRITEOFF: 'Списання з 071',
};

export default function DebtAdjustmentsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['debt-adjustments'],
    queryFn: () => debtAdjustments.list({ limit: 200 }),
  });

  const cols = useMemo<ColumnDef<DebtAdjustmentListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: '№',
        width: 150,
        importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date
            ? new Date(r.document_date).toLocaleString('uk-UA')
            : '',
      },
      {
        key: 'adjustment_type',
        header: 'Тип',
        width: 160,
        sortValue: (r) => r.adjustment_type ?? '',
        cell: (r) => TYPE_LABEL[r.adjustment_type] ?? r.adjustment_type,
      },
      {
        key: 'total_amount',
        header: 'Сума заліку',
        align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmt2(r.total_amount),
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Коригування боргу / Взаємозалік</h2>
        <button
          className="primary"
          onClick={() => navigate('/debt-adjustments/new')}
        >
          + Новий документ
        </button>
      </div>
      <DataTable<DebtAdjustmentListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/debt-adjustments/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
